/**
 * Prioridade de abastecimento a partir do consumo real do SmartFeed.
 * Port de pacote-estoque-racao/calculo-consumo.js.
 */

export const CONFIG = {
  timezoneOffsetHours: -3,
  smoothingWindow: 5,
  refillThresholdKg: 1500,
  minHoursPerDay: 6,
  windowDays: 7,
  minValidDays: 3,
  minConsumptionKgDay: 100,
  criticalDays: 3,
  warningDays: 7,
  maxDisplayDays: 60,
  staleHours: 24,
}

export const STATE_LABELS = {
  critico: 'Crítico',
  atencao: 'Atenção',
  folga: 'Folga',
  sem_consumo: 'Sem consumo',
  sem_leitura: 'Sem leitura recente',
}

export function median(values) {
  const sorted = [...values].sort((a, b) => a - b)
  return sorted[Math.floor(sorted.length / 2)]
}

export function localHourKey(isoUtc, cfg = CONFIG) {
  const time = new Date(isoUtc).getTime() + cfg.timezoneOffsetHours * 3600e3
  return new Date(time).toISOString().slice(0, 13)
}

export function previousDays(today, count) {
  const base = new Date(`${today}T00:00:00Z`).getTime()
  return Array.from({ length: count }, (_, index) =>
    new Date(base - (count - index) * 86400e3).toISOString().slice(0, 10)
  )
}

export function saoPauloToday(now = new Date(), cfg = CONFIG) {
  return new Date(now.getTime() + cfg.timezoneOffsetHours * 3600e3).toISOString().slice(0, 10)
}

/** Histórico de D-8 até hoje; a média usa os 7 dias completos anteriores. */
export function historyWindow(today, cfg = CONFIG) {
  const lead = previousDays(today, cfg.windowDays + 1)
  return {
    start: lead[0],
    end: today,
    days: lead.slice(1),
  }
}

export function formatStaleDate(iso, cfg = CONFIG) {
  if (!iso) return null
  const time = new Date(iso).getTime()
  if (Number.isNaN(time)) return null
  const shifted = new Date(time + cfg.timezoneOffsetHours * 3600e3)
  const day = String(shifted.getUTCDate()).padStart(2, '0')
  const month = String(shifted.getUTCMonth() + 1).padStart(2, '0')
  return `${day}/${month}`
}

function readingWeight(reading) {
  const weight = Number(reading?.weight ?? reading?.last_hour_weight)
  return Number.isFinite(weight) ? weight : null
}

function readingTime(reading) {
  return reading?.datetime_fetched || reading?.last_updated || reading?.datetime || null
}

function readingUsable(reading) {
  if (!reading) return false
  if (reading.valid === false || reading.valid === 0 || reading.valid === 'false') return false
  return readingWeight(reading) != null && readingTime(reading) != null
}

export function siloDailyConsumption(history, days, cfg = CONFIG) {
  const byHour = {}
  for (const reading of history || []) {
    if (!readingUsable(reading)) continue
    const key = localHourKey(readingTime(reading), cfg)
    if (!byHour[key]) byHour[key] = []
    byHour[key].push(readingWeight(reading))
  }
  const hours = Object.keys(byHour).sort().map((key) => [key, median(byHour[key])])

  const half = Math.floor(cfg.smoothingWindow / 2)
  const smooth = hours.map((point, index) => [
    point[0],
    median(hours.slice(Math.max(0, index - half), index + half + 1).map((item) => item[1])),
  ])

  const consumption = days.map(() => 0)
  const coverage = days.map(() => 0)
  for (let index = 1; index < smooth.length; index += 1) {
    const dayIndex = days.indexOf(smooth[index][0].slice(0, 10))
    if (dayIndex < 0) continue
    const delta = smooth[index][1] - smooth[index - 1][1]
    if (delta > cfg.refillThresholdKg) continue
    consumption[dayIndex] += -delta
  }
  for (const [key] of smooth) {
    const dayIndex = days.indexOf(key.slice(0, 10))
    if (dayIndex >= 0) coverage[dayIndex] += 1
  }

  return consumption.map((value, index) => (
    coverage[index] >= cfg.minHoursPerDay ? Math.max(0, Math.round(value)) : null
  ))
}

export function barnPriority(barn, today, now = new Date(), cfg = CONFIG) {
  const days = previousDays(today, cfg.windowDays)
  const perSilo = (barn.silos || []).map((silo) => siloDailyConsumption(silo.history || [], days, cfg))

  const daily = days.map((_, index) => {
    const values = perSilo.map((series) => series[index])
    return values.every((value) => value == null)
      ? null
      : values.reduce((sum, value) => sum + (value || 0), 0)
  })
  const valid = daily.filter((value) => value != null)
  const movingAvg = valid.length >= cfg.minValidDays
    ? Math.round(valid.reduce((sum, value) => sum + value, 0) / valid.length)
    : null

  const stock = Math.round((barn.silos || []).reduce((sum, silo) => sum + (silo.lastWeight || 0), 0))

  let state
  let daysToZero = null
  if (movingAvg == null) state = 'sem_leitura'
  else if (movingAvg < cfg.minConsumptionKgDay) state = 'sem_consumo'
  else {
    daysToZero = stock / movingAvg
    state = daysToZero <= cfg.criticalDays
      ? 'critico'
      : daysToZero <= cfg.warningDays
        ? 'atencao'
        : 'folga'
  }

  const staleSilos = (barn.silos || [])
    .filter((silo) => !silo.lastFetched || now - new Date(silo.lastFetched) > cfg.staleHours * 3600e3)
    .map((silo) => ({ id: silo.id, lastFetched: silo.lastFetched || null }))

  let daysLabel = null
  if (daysToZero != null) {
    daysLabel = daysToZero > cfg.maxDisplayDays
      ? `> ${cfg.maxDisplayDays}`
      : daysToZero.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
  }

  return {
    unit: barn.unit,
    producer: barn.producer,
    barnName: barn.barnName,
    siloCount: (barn.silos || []).length,
    stock,
    movingAvg,
    daily,
    days,
    daysToZero,
    state,
    staleSilos,
    daysLabel,
  }
}

export function sortByPriority(rows) {
  const key = (row) => {
    if (row.state === 'sem_leitura') return Infinity
    if (row.state === 'sem_consumo') return 1e9 + row.stock
    return row.daysToZero
  }
  return [...rows].sort((a, b) => key(a) - key(b))
}

export function normalizeHistoryPayload(payload) {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return []
  if (Array.isArray(payload.results)) return payload.results
  if (Array.isArray(payload.profiles)) return payload.profiles
  return Object.values(payload).filter((item) => (
    item && typeof item === 'object' && ('weight' in item || 'datetime_fetched' in item)
  ))
}
